import { forwardRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useApolloClient, useQuery } from "@apollo/client";
import { ButtonsList, IconS3 } from "@aqar-app/aqar-design-system";
import Link from "next/link";
import config from "../../common/config";
import {
  getRegionLevelVars,
  getRegionList,
  getRegionNextLevelQuery,
} from "../../graphql/queries/regions";
import { URL_LEVELS } from "../../common/constants";
import { pathLevelProp } from "../../proptypes";
import styles from "./listings.module.scss";
import { useRegionsState } from "../../contexts/regions/regions.provider";
import { addDailyPrefix } from "../../lib/dailyRentHelper";
import { useAppState } from "../../contexts/app/app.provider";

const { ButtonsListItem } = ButtonsList;
const RegionsList = forwardRef(
  (
    {
      category,
      pathLevel,
      pathParams,
      isBottom,
      showMore,
      handleShowMore,
      initRegionsList,
      regionsLinks,
    },
    ref
  ) => {
    const apolloClient = useApolloClient();
    const isDailyRentPage = useAppState("isDailyRentPage");
    const regions = useRegionsState("regions");

    const renderCondition =
      category &&
      // category !== "عقارات" &&
      (pathLevel || {}).level !== URL_LEVELS.DISTRICT;

    const suffixDataAttribute = (str) =>
      isBottom ? `${str}-bottom` : `${str}-top`;

    const {
      data: RegionsData,
      loading: regionsLoading,
      error,
    } = useQuery(getRegionNextLevelQuery(pathLevel, pathParams), {
      skip: !renderCondition && !regionsLinks,
      variables: getRegionLevelVars(category, pathLevel, apolloClient, regions),
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "network-only",
    });

    const { regionList, totalRegions } = useMemo(() => {
      let list = [];
      if (regionsLinks) {
        list = regionsLinks;
      } else {
        list = RegionsData
          ? getRegionList(RegionsData, pathLevel, pathParams)
          : initRegionsList;
      }

      return {
        regionList: showMore ? list : list?.slice(0, config.REGION_HEADER_SIZE),
        totalRegions: list?.length,
      };
    }, [showMore, RegionsData, pathLevel, pathParams]);

    if (error) return null;

    // if (regionsLoading) {
    //   return (
    //     renderCondition && (
    //       <div className={styles.container}>
    //         <ButtonsList className={styles.regionsList}>
    //           {[...Array(config.REGION_HEADER_SIZE).keys()].map((_, index) => (
    //             // eslint-disable-next-line react/no-array-index-key
    //             <ButtonsListItem
    //               key={`listingItem-${index}`}
    //               loading
    //               className={styles.regionItem}
    //               buttonClassName={styles.regionBtn}
    //             />
    //           ))}
    //         </ButtonsList>
    //       </div>
    //     )
    //   );
    // }

    return (
      renderCondition && (
        <div className={styles.container} ref={ref}>
          <ButtonsList data-cy={suffixDataAttribute("regions-list")}>
            {regionList?.map(({ path, uri, count, title }) => {
              return (
                <Link
                  href={addDailyPrefix(
                    isDailyRentPage,
                    category === "شقق-للإيجار" ? `${path}?rent_type=3` : path
                  )}
                  key={uri}
                  scroll
                  passHref
                >
                  <ButtonsListItem
                    className={styles.regionItem}
                    buttonClassName={styles.regionBtn}
                    count={count}
                    // hidden={
                    //   index > config.REGION_HEADER_SIZE - 1 &&
                    //   !(showMore && isBottom)
                    // }
                  >
                    {title}
                  </ButtonsListItem>
                </Link>
              );
            })}
            {totalRegions > config.REGION_HEADER_SIZE &&
              !(showMore && isBottom) && (
                <ButtonsListItem
                  className={styles.regionItem}
                  buttonClassName={styles.regionBtn}
                  onClick={() => {
                    handleShowMore();
                  }}
                  data-cy={suffixDataAttribute("display-all-regions")}
                >
                  <IconS3
                    type="Add"
                    width={13}
                    height={13}
                    className={styles.addIcon}
                  />
                </ButtonsListItem>
              )}
          </ButtonsList>
        </div>
      )
    );
  }
);

RegionsList.propTypes = {
  category: PropTypes.string,
  pathLevel: pathLevelProp,
  pathParams: PropTypes.arrayOf(PropTypes.string),
  isBottom: PropTypes.bool,
  handleShowMore: PropTypes.func,
  showMore: PropTypes.bool,
  initRegionsList: PropTypes.arrayOf(PropTypes.shape({})),
  regionsLinks: PropTypes.arrayOf(PropTypes.shape({})),
};

RegionsList.defaultProps = {
  category: "",
  pathLevel: {},
  isBottom: false,
  showMore: false,
  pathParams: [],
  initRegionsList: undefined,
  regionsLinks: undefined,
  handleShowMore: () => {},
};

export default RegionsList;
